import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { CornerDownLeft, Search } from 'lucide-react'

import { Dialog, DialogContent, DialogTitle } from '@/components/ui'
import { cn } from '@/lib/cn'
import { useVisibleNavSections } from '@/config/navModel'
import { buildCommandActions } from '@/config/commandActions'
import { usePermissions } from '@/hooks/usePermissions'
import { useUIStore } from '@/stores/useUIStore'

export interface CommandPaletteProps {
  open: boolean
  onClose: () => void
}

type PaletteEntry = {
  id: string
  label: string
  group: string
  icon: React.ComponentType<{ className?: string }>
  keywords: string[]
  run: () => void
}

/**
 * Ranks an entry against a lowercased query. Higher is better; -1 = no match.
 * Prefix matches on the label beat substring matches beat keyword-only matches.
 */
function scoreEntry(entry: PaletteEntry, q: string): number {
  const label = entry.label.toLowerCase()
  if (label === q) return 100
  if (label.startsWith(q)) return 80
  if (label.includes(q)) return 60
  if (entry.keywords.some((k) => k.toLowerCase().includes(q))) return 40
  return -1
}

/**
 * Command palette shell. Mounts the stateful body only while open so query and
 * highlight reset naturally on each open (no reset effects).
 */
export function CommandPalette({ open, onClose }: CommandPaletteProps): React.ReactElement {
  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent
        showCloseButton={false}
        className="top-[12%] w-[calc(100%-2rem)] max-w-xl translate-y-0 sm:top-[14%]"
        aria-label="Command palette"
      >
        <DialogTitle className="sr-only">Command palette</DialogTitle>
        {open && <CommandPaletteBody onClose={onClose} />}
      </DialogContent>
    </Dialog>
  )
}

function CommandPaletteBody({ onClose }: { onClose: () => void }): React.ReactElement {
  const navigate = useNavigate()
  const { can } = usePermissions()
  const theme = useUIStore((s) => s.theme)
  const sections = useVisibleNavSections()

  const [query, setQuery] = useState('')
  const [highlighted, setHighlighted] = useState(0)
  const listRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  // Focus the search input when the body mounts (i.e. when the palette opens).
  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  // All selectable entries (pages + actions), flattened with their group label.
  const entries = useMemo<PaletteEntry[]>(() => {
    const pageEntries: PaletteEntry[] = sections.flatMap((section) =>
      section.items.map((item) => ({
        id: `nav:${item.to}`,
        label: item.label,
        group: section.label,
        icon: item.icon,
        keywords: [item.to.replace(/[/-]/g, ' ').trim()],
        run: () => navigate(item.to),
      }))
    )
    const actionEntries: PaletteEntry[] = buildCommandActions({ navigate, theme, can }).map(
      (action) => ({
        id: `action:${action.id}`,
        label: action.label,
        group: 'Actions',
        icon: action.icon,
        keywords: action.keywords ?? [],
        run: action.perform,
      })
    )
    return [...pageEntries, ...actionEntries]
  }, [sections, navigate, theme, can])

  // Filter + rank against the query.
  const results = useMemo<PaletteEntry[]>(() => {
    const q = query.trim().toLowerCase()
    if (!q) return entries
    return entries
      .map((entry) => ({ entry, score: scoreEntry(entry, q) }))
      .filter((r) => r.score >= 0)
      .sort((a, b) => b.score - a.score)
      .map((r) => r.entry)
  }, [entries, query])

  // Clamp the highlight to the current result set at read time (no effect needed).
  const activeIndex = results.length === 0 ? 0 : Math.min(highlighted, results.length - 1)

  // Group results in display order while preserving rank within each group.
  const grouped = useMemo<Array<{ group: string; items: PaletteEntry[] }>>(() => {
    const order: string[] = []
    const map = new Map<string, PaletteEntry[]>()
    for (const entry of results) {
      if (!map.has(entry.group)) {
        map.set(entry.group, [])
        order.push(entry.group)
      }
      map.get(entry.group)!.push(entry)
    }
    return order.map((group) => ({ group, items: map.get(group)! }))
  }, [results])

  // Scroll the highlighted row into view (DOM sync only).
  useEffect(() => {
    const el = listRef.current?.querySelector<HTMLElement>('[data-active="true"]')
    el?.scrollIntoView({ block: 'nearest' })
  }, [activeIndex, results.length])

  const runEntry = (entry: PaletteEntry | undefined): void => {
    if (!entry) return
    onClose()
    entry.run()
  }

  const handleKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlighted(results.length === 0 ? 0 : (activeIndex + 1) % results.length)
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlighted(results.length === 0 ? 0 : (activeIndex - 1 + results.length) % results.length)
    } else if (e.key === 'Enter') {
      e.preventDefault()
      runEntry(results[activeIndex])
    } else if ((e.key === 'k' || e.key === 'K') && (e.metaKey || e.ctrlKey)) {
      // Cmd/Ctrl+K also closes when the palette is open.
      e.preventDefault()
      onClose()
    }
  }

  // Flat index → maps grouped rows back to the highlighted position.
  let flatIndex = -1

  return (
    <div onKeyDown={handleKeyDown}>
      <div className="flex items-center gap-3 border-b border-gray-100 px-4 dark:border-gray-700">
        <Search className="h-4.5 w-4.5 shrink-0 text-gray-400" aria-hidden />
        <input
          ref={inputRef}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value)
            setHighlighted(0)
          }}
          type="text"
          placeholder="Search pages and actions…"
          className="h-12 w-full bg-transparent text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none dark:text-gray-100 dark:placeholder:text-gray-500"
          aria-label="Search pages and actions"
          autoComplete="off"
          spellCheck={false}
        />
      </div>

      <div ref={listRef} className="max-h-[min(60vh,420px)] overflow-y-auto py-2">
        {results.length === 0 ? (
          <p className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
            No results for “{query}”
          </p>
        ) : (
          grouped.map((section) => (
            <div key={section.group} className="mb-1">
              <p className="px-4 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-gray-400 dark:text-gray-500">
                {section.group}
              </p>
              {section.items.map((entry) => {
                flatIndex += 1
                const index = flatIndex
                const isActive = index === activeIndex
                const Icon = entry.icon
                return (
                  <button
                    key={entry.id}
                    type="button"
                    data-active={isActive}
                    onClick={() => runEntry(entry)}
                    onMouseMove={() => setHighlighted(index)}
                    className={cn(
                      'flex w-full items-center gap-3 px-4 py-2 text-left text-sm transition-colors',
                      isActive
                        ? 'bg-primary/10 text-primary dark:bg-primary-light/15 dark:text-primary-light'
                        : 'text-gray-700 dark:text-gray-200'
                    )}
                  >
                    <Icon className="h-4 w-4 shrink-0 opacity-70" aria-hidden />
                    <span className="min-w-0 flex-1 truncate">{entry.label}</span>
                    {isActive && (
                      <CornerDownLeft className="h-3.5 w-3.5 shrink-0 opacity-60" aria-hidden />
                    )}
                  </button>
                )
              })}
            </div>
          ))
        )}
      </div>

      <div className="flex items-center gap-4 border-t border-gray-100 px-4 py-2 text-[11px] text-gray-400 dark:border-gray-700 dark:text-gray-500">
        <span className="flex items-center gap-1">
          <Kbd>↑</Kbd>
          <Kbd>↓</Kbd>
          navigate
        </span>
        <span className="flex items-center gap-1">
          <Kbd>↵</Kbd>
          open
        </span>
        <span className="flex items-center gap-1">
          <Kbd>esc</Kbd>
          close
        </span>
      </div>
    </div>
  )
}

function Kbd({ children }: { children: React.ReactNode }): React.ReactElement {
  return (
    <kbd className="inline-flex min-w-[18px] items-center justify-center rounded border border-gray-200 bg-gray-50 px-1 py-0.5 font-sans text-[10px] font-medium text-gray-500 dark:border-gray-600 dark:bg-gray-700/60 dark:text-gray-300">
      {children}
    </kbd>
  )
}

CommandPalette.displayName = 'CommandPalette'
